import { FC, useState } from "react"
import { Button } from "@/components/ui/button"
import { motion, AnimatePresence } from "framer-motion"
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import {
	Pagination,
	PaginationContent,
	PaginationEllipsis,
	PaginationItem,
	PaginationLink,
	PaginationNext,
	PaginationPrevious,
} from "@/components/ui/pagination"
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select"
import { Trash2, Loader2 } from "lucide-react"
import { RelevantDocumentCard } from "./RelevantDocumentCard"
import { usePaginatedDocuments } from "@/hooks/usePaginatedDocuments"
import { useBatchDocumentStages } from "@/hooks/useBatchDocumentStages"

type PaginatedRelevantDocumentsListProps = {
	caseId: string
	onDeleteDocument: (documentId: string) => Promise<void>
	onBatchDeleteDocuments: (documentIds: string[]) => Promise<void>
}

export const PaginatedRelevantDocumentsList: FC<
	PaginatedRelevantDocumentsListProps
> = ({ caseId, onDeleteDocument, onBatchDeleteDocuments }) => {
	const [currentPage, setCurrentPage] = useState(1)
	const [pageSize, setPageSize] = useState(6)
	const [deleteMultipleSelected, setDeleteMultipleSelected] = useState(false)
	const [selectedDocumentIds, setSelectedDocumentIds] = useState<Set<string>>(
		new Set()
	)
	const [isBatchDeleting, setIsBatchDeleting] = useState(false)

	const { data, isLoading, error, refetch } = usePaginatedDocuments({
		caseId,
		page: currentPage,
		limit: pageSize,
		relevantOnly: true,
	})

	// Fetch stages for all visible documents
	const documentIds = data?.documents.map((doc) => doc.id) || []
	const { data: stagesData } = useBatchDocumentStages(caseId, documentIds)

	const handleDeleteMultipleSelected = (documentId: string) => {
		setDeleteMultipleSelected(true)
		setSelectedDocumentIds(new Set([documentId]))
	}

	const handleSelectDocument = (documentId: string) => {
		if (!deleteMultipleSelected) {
			return
		}
		setSelectedDocumentIds((prevSelected) => {
			const newSelected = new Set(prevSelected)
			if (newSelected.has(documentId)) {
				newSelected.delete(documentId)
			} else {
				newSelected.add(documentId)
			}
			return newSelected
		})
	}

	const handleConfirmBatchDelete = async () => {
		setIsBatchDeleting(true)
		const idsToDelete = Array.from(selectedDocumentIds)
		try {
			await onBatchDeleteDocuments(idsToDelete)
			setSelectedDocumentIds(new Set())
			setDeleteMultipleSelected(false)
			refetch()
		} catch (error) {
			console.error("Batch delete failed:", error)
		} finally {
			setIsBatchDeleting(false)
		}
	}

	const handlePageChange = (page: number) => {
		setCurrentPage(page)
		setSelectedDocumentIds(new Set())
		setDeleteMultipleSelected(false)
	}

	const handlePageSizeChange = (value: string) => {
		setPageSize(Number(value))
		setCurrentPage(1)
		setSelectedDocumentIds(new Set())
		setDeleteMultipleSelected(false)
	}

	const handleDeleteDocument = async (documentId: string) => {
		await onDeleteDocument(documentId)
		refetch()
	}

	if (isLoading && !data) {
		return (
			<div className="flex items-center justify-center py-12">
				<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
			</div>
		)
	}

	if (error) {
		return (
			<div className="text-center py-12 text-destructive">
				Error loading documents: {error.message}
			</div>
		)
	}

	if (!data || data.documents.length === 0) {
		return (
			<div className="text-center py-12 border rounded-xl border-dashed">
				<p className="text-muted-foreground">No relevant documents found</p>
			</div>
		)
	}

	const selectedDocuments = data.documents.filter((doc) =>
		selectedDocumentIds.has(doc.id)
	)
	const numSelected = selectedDocumentIds.size

	// Generate page numbers for pagination
	const generatePageNumbers = () => {
		const pages = []
		const totalPages = data.total_pages
		const current = currentPage

		if (totalPages <= 7) {
			// Show all pages if 7 or less
			for (let i = 1; i <= totalPages; i++) {
				pages.push(i)
			}
		} else {
			// Always show first page
			pages.push(1)

			if (current > 3) {
				pages.push(-1)
			}

			// Show pages around current
			for (
				let i = Math.max(2, current - 1);
				i <= Math.min(totalPages - 1, current + 1);
				i++
			) {
				pages.push(i)
			}

			if (current < totalPages - 2) {
				pages.push(-1)
			}

			// Always show last page
			pages.push(totalPages)
		}

		return pages
	}

	return (
		<div>
			{/* Pagination controls at top */}
			<div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6">
				<div className="flex items-center gap-2">
					<span className="text-sm text-muted-foreground">Items per page:</span>
					<Select
						value={pageSize.toString()}
						onValueChange={handlePageSizeChange}
					>
						<SelectTrigger className="w-[70px]">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="3">3</SelectItem>
							<SelectItem value="6">6</SelectItem>
							<SelectItem value="9">9</SelectItem>
							<SelectItem value="12">12</SelectItem>
							<SelectItem value="15">15</SelectItem>
							<SelectItem value="18">18</SelectItem>
							<SelectItem value="20">20</SelectItem>
						</SelectContent>
					</Select>
				</div>

				<Pagination>
					<PaginationContent>
						<PaginationItem>
							<PaginationPrevious
								onClick={() => handlePageChange(currentPage - 1)}
								className={
									currentPage === 1
										? "pointer-events-none opacity-50"
										: "cursor-pointer"
								}
							/>
						</PaginationItem>

						{generatePageNumbers().map((pageNum, idx) =>
							pageNum === -1 ? (
								<PaginationItem key={`ellipsis-${idx}`}>
									<PaginationEllipsis />
								</PaginationItem>
							) : (
								<PaginationItem key={pageNum}>
									<PaginationLink
										onClick={() => handlePageChange(pageNum)}
										isActive={pageNum === currentPage}
										className="cursor-pointer"
									>
										{pageNum}
									</PaginationLink>
								</PaginationItem>
							)
						)}

						<PaginationItem>
							<PaginationNext
								onClick={() => handlePageChange(currentPage + 1)}
								className={
									currentPage === data.total_pages
										? "pointer-events-none opacity-50"
										: "cursor-pointer"
								}
							/>
						</PaginationItem>
					</PaginationContent>
				</Pagination>
			</div>

			{/* Header with delete controls */}
			<div className="mb-4 flex justify-between items-center">
				<div className="text-sm text-muted-foreground">
					Showing {(currentPage - 1) * pageSize + 1}-
					{Math.min(currentPage * pageSize, data.total)} of {data.total}{" "}
					relevant documents
				</div>

				{deleteMultipleSelected && (
					<div className="flex items-center gap-2">
						<AlertDialog>
							<AlertDialogTrigger asChild>
								<Button variant="destructive" disabled={isBatchDeleting}>
									<Trash2 className="mr-2 h-4 w-4" />
									Delete Selected ({numSelected}){isBatchDeleting && "..."}
								</Button>
							</AlertDialogTrigger>
							<AlertDialogContent>
								<AlertDialogHeader>
									<AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
									<AlertDialogDescription>
										This action cannot be undone. This will permanently delete
										the following {numSelected} document(s):
										<ul className="mt-2 list-disc list-inside max-h-60 overflow-y-auto">
											{selectedDocuments.map((doc) => (
												<li key={doc.id} className="truncate">
													{doc.name}
												</li>
											))}
										</ul>
									</AlertDialogDescription>
								</AlertDialogHeader>
								<AlertDialogFooter>
									<AlertDialogCancel disabled={isBatchDeleting}>
										Cancel
									</AlertDialogCancel>
									<AlertDialogAction
										onClick={handleConfirmBatchDelete}
										disabled={isBatchDeleting}
										className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
									>
										{isBatchDeleting ? "Deleting..." : "Yes, delete"}
									</AlertDialogAction>
								</AlertDialogFooter>
							</AlertDialogContent>
						</AlertDialog>
						<Button
							variant="outline"
							onClick={() => {
								setDeleteMultipleSelected(false)
								setSelectedDocumentIds(new Set())
							}}
						>
							Cancel
						</Button>
					</div>
				)}
			</div>

			{/* Document grid with animated height */}
			<motion.div
				transition={{ duration: 0.3, ease: "easeInOut" }}
				className="mb-6"
			>
				<AnimatePresence mode="wait">
					<motion.div
						key={currentPage}
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: -10 }}
						transition={{ duration: 0.2 }}
						className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-auto items-start"
					>
						{data.documents.map((document, index) => (
							<RelevantDocumentCard
								key={document.id}
								document={document}
								caseId={caseId}
								onDelete={() => handleDeleteDocument(document.id)}
								onDeleteMultipleSelected={() =>
									handleDeleteMultipleSelected(document.id)
								}
								isSelected={selectedDocumentIds.has(document.id)}
								onSelect={() => handleSelectDocument(document.id)}
								index={index}
								stagesData={stagesData?.[document.id]}
							/>
						))}
					</motion.div>
				</AnimatePresence>
			</motion.div>
		</div>
	)
}
